import { Link } from "react-router-dom";
import { ArrowRight, HandHeart, Truck, Sofa } from "lucide-react";
import PageHero from "@/components/PageHero";
import { FadeUp, Overline } from "@/components/Reveal";
import Marquee from "@/components/Marquee";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const CHAPTERS = [
  {
    n: "01",
    icon: HandHeart,
    title: "Community Donates",
    text: "People across Glendale and the West Valley donate gently used furniture, household essentials, home décor, or monetary support. Every couch, lamp, and set of dishes begins its second life here.",
    img: "https://images.unsplash.com/photo-1581573950452-5a438c5f390f?w=1000&q=80&auto=format&fit=crop",
    alt: "Volunteers preparing donated home goods",
  },
  {
    n: "02",
    icon: Truck,
    title: "We Collect & Prepare",
    text: "Fresh Start Furnishings coordinates donation pickup, drop-off, storage, and preparation — cleaning, inspecting, and organizing items so they're ready for a new home.",
    img: "https://images.unsplash.com/photo-1783473007464-1dbf2ff30dec?w=1000&q=80&auto=format&fit=crop",
    alt: "Delivery and pickup of donated furniture",
  },
  {
    n: "03",
    icon: Sofa,
    title: "Families Shop Affordably",
    text: "Women and families rebuilding their homes browse available inventory and choose items that fit their needs — at deeply discounted prices, with our goal of furnishing an entire home for $500 or less.",
    img: "https://images.unsplash.com/photo-1786396798391-8c3f330cf3a8?w=1000&q=80&auto=format&fit=crop",
    alt: "A family unpacking and settling into their new home",
  },
];

const FAQS = [
  {
    q: "Is Fresh Start Furnishings a free furniture giveaway?",
    a: "No. Items are offered at deeply affordable prices. The funds collected help cover storage, truck rentals, transportation, pickups, cleaning, delivery, and other program expenses — so we can keep helping more families.",
  },
  {
    q: "Who can shop through the program?",
    a: "Women and families rebuilding their lives after domestic violence, homelessness, addiction recovery, poverty and financial hardship, transitional housing, or other major life transitions. Start with our short application.",
  },
  {
    q: "What does the $500 promise mean?",
    a: "Our goal is that a family pays no more than $500 to help furnish an entire home, based on household needs, available inventory, and program guidelines. Individual items are priced separately at low, affordable prices.",
  },
  {
    q: "Where do you pick up donations?",
    a: "We serve Glendale, the West Valley, and surrounding areas. Pickup availability depends on location, scheduling, transportation, and program capacity — tell us where you are in the donation form.",
  },
  {
    q: "What items do you accept?",
    a: "Gently used furniture, household essentials, and home décor in clean, usable condition. We may accept or decline donations based on condition, safety, current needs, storage space, and transportation availability.",
  },
];

export default function HowItWorks() {
  return (
    <div data-testid="how-it-works-page">
      <PageHero overline="The Process" title="HOW A DONATION" italic="becomes a home." testid="how-it-works-headline">
        Donated by the community. Offered at affordable prices. Reinvested into helping the next family start over.
      </PageHero>

      <section className="mx-auto max-w-7xl space-y-20 px-4 py-20 sm:px-6">
        {CHAPTERS.map((c, i) => (
          <FadeUp key={c.n}>
            <div className={`grid items-center gap-10 lg:grid-cols-2 ${i % 2 === 1 ? "lg:[&>*:first-child]:order-2" : ""}`}>
              <div>
                <div className="flex items-baseline gap-4">
                  <span className="font-serif text-6xl italic text-burgundy/30">{c.n}</span>
                  <c.icon className="h-8 w-8 text-olive" />
                </div>
                <h2 className="mt-4 font-serif text-3xl tracking-tight text-olive sm:text-4xl">{c.title}</h2>
                <p className="mt-4 max-w-lg text-base leading-relaxed text-moss">{c.text}</p>
              </div>
              <img src={c.img} alt={c.alt} className="aspect-[4/3] w-full rounded-3xl border border-linen object-cover shadow-xl shadow-olive/10" />
            </div>
          </FadeUp>
        ))}
      </section>

      <Marquee />

      <section className="mx-auto max-w-3xl px-4 py-24 sm:px-6" data-testid="faq-section">
        <FadeUp className="text-center">
          <Overline className="!text-center">Questions, Answered</Overline>
          <h2 className="mt-3 font-serif text-3xl tracking-tight text-olive sm:text-4xl">Frequently asked questions</h2>
        </FadeUp>
        <FadeUp delay={0.15} className="mt-10">
          <Accordion type="single" collapsible className="space-y-3">
            {FAQS.map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`} data-testid={`faq-item-${i}`} className="rounded-2xl border border-linen bg-white/60 px-6">
                <AccordionTrigger data-testid={`faq-trigger-${i}`} className="text-left font-serif text-lg text-olive hover:no-underline">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-sm leading-relaxed text-moss">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </FadeUp>
        <FadeUp delay={0.25} className="mt-12 flex flex-wrap justify-center gap-3">
          <Link to="/donate-items" data-testid="hiw-donate-button" className="btn-accent">Donate Items <ArrowRight className="h-4 w-4" /></Link>
          <Link to="/apply-to-shop" data-testid="hiw-apply-button" className="btn-primary">Apply to Shop</Link>
        </FadeUp>
      </section>
    </div>
  );
}
